/**
 * Collapsible ad group card: name + promotion type, audience (locations,
 * placements, gender, ages), delivery (budget, schedule, optimisation goal,
 * billing event, bidding + the goal-specific extras TikTok demands: pixel /
 * frequency cap / app), start status, then the nested ad cards.
 *
 * The card stays mounted while collapsed so its effects (region fetch per
 * placement, goal snapping, defaults) keep the draft valid.
 */
import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  AlertTriangle, Check, ChevronDown, ChevronRight, Copy, Layers, Loader2, Lock, MapPin, Plus, Trash2,
} from 'lucide-react';
import { useTiktokWorkspace, type WorkspaceAdGroup } from '@/contexts/TiktokWorkspaceContext';
import { tiktokService, type TiktokPixelOption } from '@/services/tiktok.service';
import { useTiktokAccountCurrency } from '@/hooks/useTiktokAccountCurrency';
import { formatBudgetHint } from '@/utils/adPlatformMinBudgets';
import { FormField } from '@/components/FormField';
import { Button } from '@/components/Button';
import { TIKTOK_AGE_GROUPS, TIKTOK_COUNTRIES, TIKTOK_GENDERS } from './tiktokGeo';
import {
  DEFAULT_FREQUENCY_CAP, defaultGoalFor, goalSpec, goalsForObjective, objectiveSpec, promotionTypeFor,
  type TiktokBidType,
} from '@/lib/tiktok-matrix';
import { TiktokLocationPicker, type TiktokRegion } from './TiktokLocationPicker';
import { AdCard } from './AdCard';
import { adGroupIssues, adIssues } from './validation';
import { Pill, groupForm, inputCls, type Identity } from './workspaceShared';
import { Select } from '@/components/Select';

type BidType = TiktokBidType;
type AdGroupBudgetMode = 'BUDGET_MODE_DAY' | 'BUDGET_MODE_TOTAL';
type ScheduleType = 'SCHEDULE_FROM_NOW' | 'SCHEDULE_START_END';

const NO_EVENTS_WARNING =
  'This pixel has no events configured yet. Create the event in TikTok Events Manager, then reload.';

const PLACEMENT_OPTIONS = [
  { value: 'PLACEMENT_TIKTOK',            label: 'TikTok',            hint: 'In-feed video — the default surface.' },
  { value: 'PLACEMENT_PANGLE',            label: 'Pangle',            hint: 'TikTok\'s third-party publisher network.' },
  { value: 'PLACEMENT_GLOBAL_APP_BUNDLE', label: 'Global app bundle', hint: 'Cross-publisher placements — broadest reach.' },
];

const BID_TYPES: Array<{ value: BidType; label: string; hint: string }> = [
  { value: 'BID_TYPE_NO_BID', label: 'Auto bid',              hint: 'TikTok sets the bid to spend the budget efficiently.' },
  { value: 'BID_TYPE_CUSTOM', label: 'Manual bid (cost cap)', hint: 'You set a target cost-per-result.' },
];

const PROMOTION_LABELS: Record<string, string> = {
  WEBSITE: 'Website', LEAD_GENERATION: 'Lead generation', APP_ANDROID: 'App (Android)', APP_IOS: 'App (iOS)',
};

function dtLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export interface AdGroupCardProps {
  group: WorkspaceAdGroup;
  index: number;
  objective?: string;
  open: boolean;
  onToggle: () => void;
  identities: Identity[];
  identitiesLoading: boolean;
  reloadIdentities: () => Promise<unknown>;
  leadForms: Array<{ pageId: string; title: string }> | null;
  leadFormsLoading: boolean;
  reloadLeadForms: () => Promise<unknown>;
  activeAdId: string | null;
  onFocusAd: (adId: string) => void;
  onDelete: () => void;
  onDuplicate: () => void;
}

export const AdGroupCard: React.FC<AdGroupCardProps> = ({
  group, index, objective, open, onToggle,
  identities, identitiesLoading, reloadIdentities, leadForms, leadFormsLoading, reloadLeadForms,
  activeAdId, onFocusAd, onDelete, onDuplicate,
}) => {
  const { updateAdGroupForm, addAd, deleteAd, duplicateAd } = useTiktokWorkspace();
  const { currency } = useTiktokAccountCurrency();
  const locked = group.isLocked;
  const [panel, setPanel] = useState<'setup' | 'ads'>('setup');
  useEffect(() => { if (activeAdId && group.ads.some((a) => a.id === activeAdId)) setPanel('ads'); }, [activeAdId, group.ads]);

  const f = groupForm(group) as Record<string, string | string[]>;
  const set = (key: string, val: unknown) =>
    updateAdGroupForm(group.id, { [key]: val } as Record<string, unknown>);

  // ── Regions: every advertiser has its own targetable set, PER placement
  // (live-verified: TikTok 13, Pangle 10, Global App Bundle 9 on this
  // advertiser). Fetch for each selected placement and intersect. TikTok's
  // /tool/region/ sometimes answers a "soft empty" list — fall back to the
  // curated TIKTOK_COUNTRIES so the picker still works; TikTok re-validates
  // location_ids at adgroup/create.
  const [regions, setRegions] = useState<TiktokRegion[] | null>(null);
  const [regionsAreFallback, setRegionsAreFallback] = useState(false);
  const [regionsError, setRegionsError] = useState<string | null>(null);
  const placementsSel = (() => {
    const arr = Array.isArray(f.placements) ? (f.placements as string[]) : ['PLACEMENT_TIKTOK'];
    return arr.length ? arr : ['PLACEMENT_TIKTOK'];
  })();
  const placementKey = [...placementsSel].sort().join(',');
  useEffect(() => {
    let cancelled = false;
    const fallback: TiktokRegion[] = TIKTOK_COUNTRIES.map((c) => ({ locationId: c.id, name: c.label, regionCode: c.code }));
    setRegionsError(null);
    setRegions(null);
    setRegionsAreFallback(false);
    const placements = placementKey.split(',').filter(Boolean);
    Promise.all(placements.map((placement) =>
      tiktokService.listRegions({ objectiveType: objective || 'TRAFFIC', placement })))
      .then((lists) => {
        if (cancelled) return;
        const [first, ...rest] = lists;
        const intersection = (first ?? []).filter((r) => rest.every((l) => l.some((x) => x.locationId === r.locationId)));
        if (lists.some((list) => list.length === 0)) { setRegions(fallback); setRegionsAreFallback(true); }
        else {
          setRegions(intersection);
          if (intersection.length === 0) setRegionsError('These placements have no shared targetable countries. Choose a different placement combination.');
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setRegions(fallback);
        setRegionsAreFallback(true);
        const msg = (e as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
          ?? (e as { message?: string })?.message ?? 'Unknown error';
        setRegionsError(`Could not load countries from TikTok (${msg}) — showing built-in list. TikTok will validate at publish.`);
      });
    return () => { cancelled = true; };
  }, [objective, placementKey]);

  // ── Matrix-driven optimisation goal ─────────────────────────────────────
  const objSpec = objectiveSpec(objective);
  const goals = goalsForObjective(objective).filter(goal => !goal.requires.includes('APP_MMP'));
  const goalCode = (f.optimizationGoal as string) ?? '';
  const selectedGoal = goalSpec(objective, goalCode || defaultGoalFor(objective));
  const needsPixel = selectedGoal?.requires.includes('PIXEL_EVENT') ?? false;
  const needsFrequency = selectedGoal?.requires.includes('FREQUENCY_CAP') ?? false;
  const needsApp = selectedGoal?.requires.includes('APP') ?? false;
  const needsAppMmp = selectedGoal?.requires.includes('APP_MMP') ?? false;

  // Snap a now-invalid goal back to the objective's default.
  useEffect(() => {
    if (locked || goals.length === 0) return;
    if (goalCode && goals.some(goal => goal.goal === goalCode)) return;
    set('optimizationGoal', defaultGoalFor(objective));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objective, group.id, goalCode, locked]);

  const [apps, setApps] = useState<Array<{ appId: string; appName: string; platform: string }> | null>(null);
  useEffect(() => {
    if (!needsApp || apps !== null) return;
    tiktokService.listApps().then(setApps).catch(() => setApps([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsApp, apps]);

  const [pixels, setPixels] = useState<TiktokPixelOption[] | null>(null);
  const [pixelsLoading, setPixelsLoading] = useState(false);
  useEffect(() => {
    if (!needsPixel || pixels !== null || pixelsLoading) return;
    setPixelsLoading(true);
    tiktokService.listPixelOptions().then(setPixels).catch(() => setPixels([])).finally(() => setPixelsLoading(false));
  }, [needsPixel, pixels, pixelsLoading]);

  // REACH is rejected without a frequency cap — seed TikTok's own default.
  useEffect(() => {
    if (locked || !needsFrequency) return;
    const patch: Record<string, unknown> = {};
    if (!f.frequency) patch.frequency = String(DEFAULT_FREQUENCY_CAP.frequency);
    if (!f.frequencySchedule) patch.frequencySchedule = String(DEFAULT_FREQUENCY_CAP.frequencySchedule);
    if (Object.keys(patch).length > 0) updateAdGroupForm(group.id, patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsFrequency, group.id, locked]);

  // Schedule defaults the first time the group exists.
  useEffect(() => {
    if (locked || f.scheduleStartTime) return;
    const now = Date.now();
    updateAdGroupForm(group.id, {
      scheduleStartTime: dtLocal(new Date(now + 60 * 60 * 1000)),
      scheduleEndTime: dtLocal(new Date(now + 7 * 24 * 60 * 60 * 1000)),
    } as Record<string, unknown>);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.id, locked]);

  // Seed a default country once regions load (UAE → US → first), and prune
  // ids TikTok no longer permits — sending one fails the whole save with
  // "At least 1 location ID is invalid".
  useEffect(() => {
    if (locked || !regions || regions.length === 0) return;
    const existing = Array.isArray(f.locationIds) ? (f.locationIds as string[]) : [];
    if (existing.length > 0) {
      const valid = new Set(regions.map((r) => r.locationId));
      const pruned = existing.filter((id) => valid.has(id));
      if (pruned.length !== existing.length) set('locationIds', pruned);
      if (pruned.length > 0) return;
    }
    const seed = regions.find((r) => r.regionCode === 'AE') ?? regions.find((r) => r.regionCode === 'US') ?? regions[0];
    if (seed) set('locationIds', [seed.locationId]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regions, group.id, locked]);

  const budgetHint = formatBudgetHint('tiktok', currency) ?? 'Amount is in your advertiser currency.';

  const adGroupName       = (f.adGroupName as string) ?? '';
  const optimizationGoal  = goalCode || defaultGoalFor(objective);
  // Drafts saved before BID_TYPE_MAX_CONVERSION was retired can still hold it —
  // TikTok rejects it in every combination, so treat it as auto bid.
  const bidType           = (((f.bidType as string) === 'BID_TYPE_CUSTOM' ? 'BID_TYPE_CUSTOM' : 'BID_TYPE_NO_BID') as BidType);
  const bidPrice          = (f.bidPrice as string) ?? '';
  const pixelId           = (f.pixelId as string) ?? '';
  const optimizationEvent = (f.optimizationEvent as string) ?? '';
  const appId             = (f.appId as string) ?? '';
  const appPlatform = apps?.find(app => app.appId === appId)?.platform;
  const promotionType = objective === 'APP_PROMOTION' ? (appPlatform === 'IOS' ? 'APP_IOS' : appPlatform === 'ANDROID' ? 'APP_ANDROID' : 'Choose an app') : promotionTypeFor(objective);
  const frequency         = (f.frequency as string) ?? String(DEFAULT_FREQUENCY_CAP.frequency);
  const frequencySchedule = (f.frequencySchedule as string) ?? String(DEFAULT_FREQUENCY_CAP.frequencySchedule);
  const budgetMode        = (((f.budgetMode as string) ?? 'BUDGET_MODE_DAY') as AdGroupBudgetMode);
  const budget            = (f.budget as string) ?? '';
  const scheduleType      = (((f.scheduleType as string) ?? 'SCHEDULE_FROM_NOW') as ScheduleType);
  const scheduleStartTime = (f.scheduleStartTime as string) ?? '';
  const scheduleEndTime   = (f.scheduleEndTime as string) ?? '';
  const operationStatus   = (((f.operationStatus as string) ?? 'DISABLE') as 'ENABLE' | 'DISABLE');
  const locationIds       = (Array.isArray(f.locationIds) ? f.locationIds : []) as string[];
  const ageGroups         = (Array.isArray(f.ageGroups) ? f.ageGroups : []) as string[];
  const gender            = ((f.gender as string) ?? 'GENDER_UNLIMITED');

  const selectedPixel = (pixels ?? []).find((p) => p.pixelId === pixelId) ?? null;
  const pixelHasNoEvents = !!selectedPixel && selectedPixel.events.length === 0;
  const manualBidIsConversion = selectedGoal?.manualBidField === 'conversion_bid_price';

  const toggleIn = (key: 'placements' | 'ageGroups', list: string[], val: string) =>
    set(key, list.includes(val) ? list.filter((x) => x !== val) : [...list, val]);

  const groupMissing = adGroupIssues(group, objective);
  const adsMissing = group.ads.some((a) => adIssues(a, group, objective, identities).length > 0);
  const incomplete = groupMissing.length > 0 || adsMissing || group.ads.length === 0;
  const locationNames = locationIds.map((id) => regions?.find((r) => r.locationId === id)?.name ?? id);

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-surface shadow-soft">
      {/* header */}
      <div className="flex items-center gap-2.5 border-b border-border px-4 py-3">
        <button type="button" className="flex min-w-0 flex-1 items-center gap-2.5 text-left" aria-expanded={open} onClick={onToggle}>
          {open ? <ChevronDown size={15} className="shrink-0 text-text-muted" /> : <ChevronRight size={15} className="shrink-0 text-text-muted" />}
          {locked ? <Lock size={14} className="shrink-0 text-text-muted" /> : <Layers size={15} className="shrink-0 text-text-muted" />}
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-text-primary">{adGroupName || `Ad group ${index + 1}`}</div>
            <div className="truncate text-[11px] text-text-muted">
              {locked ? 'Saved on TikTok · ' : ''}
              {group.ads.length} ad{group.ads.length === 1 ? '' : 's'}
              {locationNames.length > 0 && ` · ${locationNames.slice(0, 2).join(', ')}${locationNames.length > 2 ? ` +${locationNames.length - 2}` : ''}`}
            </div>
          </div>
        </button>
        {locked ? <Pill tone="ok"><Lock size={9} /> saved</Pill> : incomplete && <Pill tone="warn">incomplete</Pill>}
        <button type="button" onClick={onDuplicate} title="Duplicate ad group" className="flex h-7 w-7 items-center justify-center rounded-md text-text-muted hover:bg-surface-alt hover:text-text-primary">
          <Copy size={13} />
        </button>
        {!locked && (
          <button type="button" onClick={onDelete} title="Remove ad group" className="flex h-7 w-7 items-center justify-center rounded-md text-text-muted hover:bg-danger-soft hover:text-danger">
            <Trash2 size={14} />
          </button>
        )}
      </div>

      {open && (
        <div className="space-y-4 p-4">
          {locked && (
            <div className="rounded-xl border border-success/30 bg-success-soft px-3 py-2 text-xs text-success">
              This ad group already exists on TikTok — its targeting and budget are edited from Campaigns. You can still add ads below.
            </div>
          )}

          <div className="tt-tabs" aria-label="Ad group sections">
            <button type="button" aria-pressed={panel === 'setup'} onClick={() => setPanel('setup')}>Audience & budget</button>
            <button type="button" aria-pressed={panel === 'ads'} onClick={() => setPanel('ads')}>Ads ({group.ads.length})</button>
          </div>
          <div hidden={panel !== 'setup'} className="space-y-3">
          <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
            <FormField label="Ad group name" required>
              <input className={inputCls} aria-label="Ad group name" placeholder="e.g. UAE · 18–34 · Auto bid" value={adGroupName} onChange={(e) => set('adGroupName', e.target.value)} disabled={locked} />
            </FormField>
            <FormField label="Promotion type" hintBelow hint="Based on your campaign goal.">
              <input className={inputCls} value={PROMOTION_LABELS[promotionType] ?? promotionType} disabled readOnly />
            </FormField>
          </div>

          {/* audience */}
          <div className="space-y-4 rounded-xl border border-border p-3.5">
            <div>
              <div className="flex items-center gap-1.5 text-xs font-semibold text-text-primary"><MapPin size={12} className="text-primary" /> Locations</div>
              <p className="mb-2 mt-0.5 text-[11px] text-text-muted">
                {regionsAreFallback
                  ? 'TikTok\'s region API returned no data for this advertiser — showing a built-in country list. TikTok validates at publish.'
                  : `Only places TikTok lets this advertiser target for a ${objSpec?.label ?? 'Traffic'} campaign on the selected placements.`}
              </p>
              {regionsError && <div className="mb-2 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2 text-2xs text-warning">{regionsError}</div>}
              <TiktokLocationPicker
                regions={regions ?? []}
                loading={regions === null}
                value={locationIds}
                onChange={(next) => set('locationIds', next)}
                disabled={locked}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <div className="text-xs font-semibold text-text-primary">Placements</div>
                <div className="mt-2 space-y-1.5">
                  {PLACEMENT_OPTIONS.map((p) => {
                    const on = placementsSel.includes(p.value);
                    return (
                      <button
                        key={p.value}
                        type="button"
                        disabled={locked}
                        aria-pressed={on}
                        onClick={() => toggleIn('placements', placementsSel, p.value)}
                        className={clsx('flex w-full items-start gap-2 rounded-lg px-1.5 py-1 text-left text-xs transition-colors disabled:opacity-60', on ? 'text-text-primary' : 'text-text-secondary hover:bg-surface-alt')}
                        title={p.hint}
                      >
                        <span className={clsx(
                          'mt-px flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                          on ? 'border-primary bg-primary text-on-primary' : 'border-border-strong bg-surface',
                        )}>
                          {on && <Check size={10} strokeWidth={3} />}
                        </span>
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-text-primary">Gender</div>
                <Select aria-label="Gender" className={clsx(inputCls, 'mt-2 !py-1.5 text-xs')} value={gender} onChange={(e) => set('gender', e.target.value)} disabled={locked}>
                  {TIKTOK_GENDERS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                </Select>
              </div>
              <div>
                <div className="text-xs font-semibold text-text-primary">Ages <span className="font-normal text-text-muted">(empty = all)</span></div>
                <div className="mt-2 grid grid-cols-2 gap-1.5">
                  {TIKTOK_AGE_GROUPS.map((a) => {
                    const on = ageGroups.includes(a.value);
                    return (
                      <button
                        key={a.value}
                        type="button"
                        disabled={locked}
                        aria-pressed={on}
                        onClick={() => toggleIn('ageGroups', ageGroups, a.value)}
                        className={clsx('flex items-center gap-1.5 rounded-lg px-1.5 py-1 text-left text-xs transition-colors disabled:opacity-60', on ? 'text-text-primary' : 'text-text-secondary hover:bg-surface-alt')}
                      >
                        <span className={clsx(
                          'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                          on ? 'border-primary bg-primary text-on-primary' : 'border-border-strong bg-surface',
                        )}>
                          {on && <Check size={10} strokeWidth={3} />}
                        </span>
                        {a.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* delivery */}
          <div className="space-y-3 rounded-xl border border-border p-3.5">
            {!objective && (
              <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2 text-2xs text-warning">
                <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                Pick the campaign objective first — TikTok only allows specific optimisation goals per objective.
              </div>
            )}
            {objSpec?.unavailableReason && (
              <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2 text-2xs text-warning">
                <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                TikTok can&apos;t run {objSpec.label} ad groups on this advertiser. {objSpec.unavailableReason}
              </div>
            )}

            <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2 lg:grid-cols-4">
              <FormField label="Budget" required>
                <Select aria-label="Ad group budget type" className={inputCls} value={budgetMode} onChange={(e) => set('budgetMode', e.target.value)} disabled={locked}>
                  <option value="BUDGET_MODE_DAY">Daily</option>
                  <option value="BUDGET_MODE_TOTAL">Lifetime</option>
                </Select>
              </FormField>
              <FormField label="Amount" required hintBelow hint={budgetHint}>
                <div className="relative">
                  <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-xs font-medium text-text-muted">{currency ?? '—'}</span>
                  <input aria-label="Ad group budget amount" className={`${inputCls} pl-14`} type="number" min={0} step="any" placeholder={budgetMode === 'BUDGET_MODE_DAY' ? '20' : '500'} value={budget} onChange={(e) => set('budget', e.target.value)} disabled={locked} />
                </div>
              </FormField>
              <FormField label="Schedule" required>
                <Select aria-label="Schedule" className={inputCls} value={scheduleType} onChange={(e) => set('scheduleType', e.target.value)} disabled={locked}>
                  <option value="SCHEDULE_FROM_NOW">Run continuously</option>
                  <option value="SCHEDULE_START_END">Start + end</option>
                </Select>
              </FormField>
              <FormField label="Start" required>
                <input aria-label="Start date and time" type="datetime-local" className={inputCls} value={scheduleStartTime} onChange={(e) => set('scheduleStartTime', e.target.value)} disabled={locked} />
              </FormField>
              {scheduleType === 'SCHEDULE_START_END' && (
                <FormField label="End" required>
                  <input aria-label="End date and time" type="datetime-local" className={inputCls} value={scheduleEndTime} onChange={(e) => set('scheduleEndTime', e.target.value)} disabled={locked} />
                </FormField>
              )}

              <FormField label="Optimization goal" required hintBelow hint={selectedGoal?.hint ?? 'Goals are limited to what TikTok accepts for the campaign objective.'}>
                <Select aria-label="Optimization goal" className={inputCls} value={optimizationGoal} onChange={(e) => set('optimizationGoal', e.target.value)} disabled={locked || goals.length === 0}>
                  {goals.length === 0 && <option value="">— pick a campaign objective first —</option>}
                  {goals.map((g) => <option key={g.goal} value={g.goal}>{g.label}</option>)}
                </Select>
              </FormField>
              <FormField label="Billing event" hintBelow hint="Based on the selected goal.">
                <input className={inputCls} value={selectedGoal?.billingEvent ?? '—'} disabled readOnly />
              </FormField>
              <FormField label="Bidding" required hintBelow hint={BID_TYPES.find((b) => b.value === bidType)?.hint}>
                <Select aria-label="Bidding" className={inputCls} value={bidType} onChange={(e) => set('bidType', e.target.value)} disabled={locked}>
                  {BID_TYPES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                  <option value="BID_TYPE_MAX_CONVERSION" disabled>Max conversion — not supported by TikTok for this advertiser</option>
                </Select>
              </FormField>
              {bidType === 'BID_TYPE_CUSTOM' && (
                <FormField
                  label={manualBidIsConversion ? 'Cost per conversion' : 'Bid cap'}
                  required
                  hintBelow
                  hint={manualBidIsConversion ? 'Target cost for one conversion, in your advertiser currency.' : 'Target cost per click / view / 1k impressions.'}
                >
                  <input className={inputCls} type="number" min={0} step="any" placeholder="0.50" value={bidPrice} onChange={(e) => set('bidPrice', e.target.value)} disabled={locked} />
                </FormField>
              )}
            </div>

            {needsFrequency && (
              <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
                <FormField label="Show ad at most" required hintBelow hint="TikTok rejects reach ad groups without a cap.">
                  <div className="relative">
                    <input className={inputCls} type="number" min={1} step={1} value={frequency} onChange={(e) => set('frequency', e.target.value)} disabled={locked} />
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-2xs text-text-muted">times</span>
                  </div>
                </FormField>
                <FormField label="every" required hintBelow hint={`Default is ${DEFAULT_FREQUENCY_CAP.frequency} per ${DEFAULT_FREQUENCY_CAP.frequencySchedule} days.`}>
                  <div className="relative">
                    <input className={inputCls} type="number" min={1} step={1} value={frequencySchedule} onChange={(e) => set('frequencySchedule', e.target.value)} disabled={locked} />
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-2xs text-text-muted">days</span>
                  </div>
                </FormField>
              </div>
            )}

            {needsApp && (
              <FormField label="App to promote" required hintBelow hint="TikTok sends people to this app's store listing. Apps are registered in TikTok Ads Manager → Assets → Apps.">
                <Select aria-label="App to promote" className={inputCls} value={appId} onChange={(e) => set('appId', e.target.value)} disabled={locked}>
                  <option value="">{apps === null ? 'Loading apps…' : '— Select an app —'}</option>
                  {(apps ?? []).map((a) => <option key={a.appId} value={a.appId}>{a.appName} ({a.platform})</option>)}
                </Select>
                {apps !== null && apps.length === 0 && (
                  <p className="mt-1 text-2xs text-warning">No app registered on this advertiser yet. Add it in TikTok Ads Manager → Assets → Apps, then reload.</p>
                )}
                {needsAppMmp && (
                  <p className="mt-1 text-2xs text-warning">
                    Install and in-app-event goals are unavailable here until the app event source can be verified. Use clicks to the app store.
                  </p>
                )}
              </FormField>
            )}

            {needsPixel && (
              <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
                <FormField label="Pixel" required hintBelow hint="TikTok optimises against events on this pixel.">
                  {pixelsLoading || pixels === null ? (
                    <div className="flex items-center gap-2 rounded-xl border border-border bg-surface-alt/40 p-3 text-xs text-text-muted"><Loader2 size={12} className="animate-spin" /> Loading pixels…</div>
                  ) : pixels.length === 0 ? (
                    <div className="rounded-xl border border-warning/40 bg-warning-soft p-3 text-2xs text-warning">No pixels on this advertiser yet. Create one under TikTok → Pixel, then reload.</div>
                  ) : (
                    <Select
                      className={inputCls}
                      value={pixelId}
                      onChange={(e) => updateAdGroupForm(group.id, { pixelId: e.target.value, optimizationEvent: '' } as Record<string, unknown>)}
                      disabled={locked}
                    >
                      <option value="">— pick a pixel —</option>
                      {pixels.map((p) => <option key={p.pixelId} value={p.pixelId}>{p.pixelName} ({p.events.length} {p.events.length === 1 ? 'event' : 'events'})</option>)}
                    </Select>
                  )}
                </FormField>
                <FormField label="Optimisation event" required hintBelow hint="Only events already configured on the pixel can be optimised for.">
                  <Select aria-label="Optimization event" className={inputCls} value={optimizationEvent} onChange={(e) => set('optimizationEvent', e.target.value)} disabled={locked || !selectedPixel || pixelHasNoEvents}>
                    <option value="">{selectedPixel ? '— pick an event —' : '— pick a pixel first —'}</option>
                    {(selectedPixel?.events ?? []).map((ev) => <option key={ev.eventType} value={ev.eventType}>{ev.label ?? ev.eventType}</option>)}
                  </Select>
                </FormField>
                {pixelHasNoEvents && (
                  <div className="sm:col-span-2 -mt-1 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2 text-2xs text-warning">
                    <AlertTriangle size={12} className="mt-0.5 shrink-0" /> {NO_EVENTS_WARNING}
                  </div>
                )}
              </div>
            )}

            <div>
              <div className="mb-1.5 text-xs font-semibold text-text-primary">Start status</div>
              <div className="flex gap-2">
                <button type="button" disabled={locked} onClick={() => set('operationStatus', 'DISABLE')}
                  className={clsx('flex-1 rounded-xl border px-3 py-1.5 text-xs transition-colors disabled:opacity-50', operationStatus === 'DISABLE' ? 'border-primary bg-primary-soft text-primary' : 'border-border bg-surface text-text-primary hover:border-primary/40')}>
                  Paused (recommended)
                </button>
                <button type="button" disabled={locked} onClick={() => set('operationStatus', 'ENABLE')}
                  className={clsx('flex-1 rounded-xl border px-3 py-1.5 text-xs transition-colors disabled:opacity-50', operationStatus === 'ENABLE' ? 'border-success bg-success-soft text-success' : 'border-border bg-surface text-text-primary hover:border-success/40')}>
                  Enabled (ad group only)
                </button>
              </div>
            </div>
          </div>

          <div className="flex justify-end"><Button size="sm" onClick={() => {
            if (groupMissing.length) { toast.error(`Add ${groupMissing.join(', ')} before continuing.`); return; }
            setPanel('ads');
            if (!group.ads.length) { const id = addAd(group.id); onFocusAd(id); }
          }}>Continue to ads <ChevronRight size={14} /></Button></div>
          </div>

          {/* ads */}
          <div hidden={panel !== 'ads'} className="space-y-3">
            {group.ads.map((ad, i) => (
              <AdCard
                key={ad.id}
                adGroup={group}
                ad={ad}
                index={i}
                objective={objective}
                identities={identities}
                identitiesLoading={identitiesLoading}
                reloadIdentities={reloadIdentities}
                leadForms={leadForms}
                leadFormsLoading={leadFormsLoading}
                reloadLeadForms={reloadLeadForms}
                focused={activeAdId === ad.id}
                onFocus={() => onFocusAd(ad.id)}
                onDelete={() => deleteAd(group.id, ad.id)}
                onDuplicate={() => duplicateAd(group.id, ad.id)}
              />
            ))}
            {group.ads.length === 0 && (
              <div className="rounded-xl border border-dashed border-border bg-surface-alt/40 px-4 py-5 text-center text-xs text-text-muted">
                This ad group has no ads yet.
              </div>
            )}
            <Button variant="secondary" size="sm" iconLeft={<Plus size={13} />} onClick={() => addAd(group.id)}>
              Add another ad
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
